import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Facebook, Linkedin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const legalLinks = [
    { to: '/privacy', label: 'Privacy Policy' },
    { to: '/terms', label: 'Terms & Conditions' },
    { to: '/cancellation-policy', label: 'Cancellation & Payment' },
    { to: '/child-safeguarding', label: 'Child Safeguarding' },
    { to: '/photo-consent', label: 'Photo & Video Consent' }
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-2">
              <img
                src="https://customer-assets.emergentagent.com/job_early-literacy-hub-3/artifacts/yw9o3e9y_image.png"
                alt="Read Right Academy Logo"
                className="h-12 w-auto bg-white p-2 rounded"
              />
              <p className="font-bold text-white text-lg leading-tight">Read Right Academy</p>
            </div>
            <p className="text-xs text-gray-400 mb-5">
              Registration No: 202203194476 (KT0527511-D)
            </p>
            <p className="text-gray-300 leading-relaxed mb-6 max-w-md">
              Building confident readers and learners through structured, child-centred, 
              and joyful early literacy education. We prepare the child first.
            </p>
            <div className="space-y-3">
              <a
                href="mailto:teacherkalah@gmail.com"
                className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
              >
                <Mail className="h-5 w-5" />
                <span>teacherkalah@gmail.com</span>
              </a>
              <a
                href="tel:+60166291687"
                className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
              >
                <Phone className="h-5 w-5" />
                <span>+60 16-629 1687</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('home')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('programs')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Programmes
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('testimonials')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Testimonials
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Our Programmes</h3>
            <ul className="space-y-3 text-gray-300">
              <li>Free Trial Class</li>
              <li>One-to-One Reading</li>
              <li>Read Together (Small Group)</li>
              <li>Phonics Teacher Training</li>
              <li>Parent Educator Programme</li>
              <li>Parent Phonics Guide</li>
            </ul>
          </div>
        </div>

        {/* Social Media + Legal */}
        <div className="border-t border-gray-700 pt-8 space-y-6">
          {/* Legal links row */}
          <div className="flex flex-wrap justify-center sm:justify-start gap-x-5 gap-y-2 text-sm">
            {legalLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`footer-${link.to.replace('/', '')}`}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Copyright + socials */}
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} Read Right Academy. All rights reserved.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.facebook.com/learneasyphonics/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-[#3B4BA0] transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.linkedin.com/in/k-kalah-kulasingam-b1749569/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-[#3B4BA0] transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;